import { getInsurerById } from "@/core/insurance/insurer-store";

export type PatientGender = "male" | "female" | "other";

/** Ambulatoire (consultation externe) vs hospitalisé. */
export type PatientType = "outpatient" | "inpatient";

/** Prise en charge tiers payant — assureur ou entreprise. */
export type PatientCoverageType = "none" | "insurer" | "company";

export interface Patient {
  id: string;
  fileNumber: string;
  firstName: string;
  lastName: string;
  gender: PatientGender;
  patientType: PatientType;
  coverageType: PatientCoverageType;
  /** Nom assureur ou entreprise si prise en charge (libellé affiché). */
  coverageName: string;
  /** Lien CRM — entreprise d'assurance. */
  insurerId: string | null;
  insurerAgentId: string | null;
  memberNumber: string;
  policyNumber: string;
  phone: string;
  address: string;
  birthDate: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface PatientInput {
  firstName: string;
  lastName: string;
  gender: PatientGender;
  patientType: PatientType;
  coverageType?: PatientCoverageType;
  coverageName?: string;
  insurerId?: string | null;
  insurerAgentId?: string | null;
  memberNumber?: string;
  policyNumber?: string;
  phone: string;
  address: string;
  birthDate?: string;
  notes?: string;
}

export const PATIENT_GENDER_LABELS: Record<PatientGender, string> = {
  male: "Homme",
  female: "Femme",
  other: "Autre",
};

export const PATIENT_TYPE_LABELS: Record<PatientType, string> = {
  outpatient: "Ambulatoire",
  inpatient: "Hospitalisé",
};

export const PATIENT_COVERAGE_LABELS: Record<PatientCoverageType, string> = {
  none: "Aucune (patient payeur)",
  insurer: "Assureur / mutuelle",
  company: "Entreprise",
};

export function hasThirdPartyCoverage(
  patient: Pick<
    Patient,
    "coverageType" | "coverageName" | "insurerId"
  >,
): boolean {
  if (patient.coverageType === "insurer" && patient.insurerId) return true;
  return (
    patient.coverageType !== "none" && patient.coverageName.trim().length > 0
  );
}

export function patientCoverageLabel(
  patient: Pick<Patient, "coverageType" | "coverageName" | "insurerId">,
): string {
  if (!hasThirdPartyCoverage(patient)) return PATIENT_COVERAGE_LABELS.none;
  if (patient.coverageType === "insurer" && patient.insurerId) {
    const ins = getInsurerById(patient.insurerId);
    if (ins) return `${PATIENT_COVERAGE_LABELS.insurer} — ${ins.name}`;
  }
  return `${PATIENT_COVERAGE_LABELS[patient.coverageType]} — ${patient.coverageName}`;
}

export function patientFullName(patient: Pick<Patient, "firstName" | "lastName">): string {
  return `${patient.lastName} ${patient.firstName}`.trim();
}

export function patientGenderLabel(gender: PatientGender): string {
  return PATIENT_GENDER_LABELS[gender];
}

export function patientTypeLabel(patientType: PatientType): string {
  return PATIENT_TYPE_LABELS[patientType];
}
